import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import React from "react";
import { OrgRowObj } from "..";
import CreateOrgModal from "./CreateOrgModal";
import DeleteOrgModal from "./DeleteOrgModal";
import Searchbox from "components/fields/Searchbox";
import Pagination from "components/pagination";
import CustomToolTip from "components/tooltips/new";
import { Link, useNavigate } from "react-router-dom";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";

const columnHelper = createColumnHelper<OrgRowObj>();

function OrgTable(props: {
  tableData: any;
  fetchOrganizations: () => void;
  fetchAllOrganizations: () => void;
  allGateways: any;
  isLoading: boolean;
  roleData: any;
  page: number;
  setPage: any;
  totalpage: number;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  setPageSize: any;
  onValueChange: (value: string) => void;
  allOrg: any;
}) {
  const {
    tableData,
    fetchOrganizations,
    fetchAllOrganizations,
    allGateways,
    roleData,
    page,
    setPage,
    totalpage,
    totalItems,
    currentPage,
    pageSize,
    setPageSize,
    onValueChange,
    allOrg,
  } = props;

  const navigate = useNavigate();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [searchVal, setSearchVal] = React.useState<any>("");
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);

  React.useEffect(() => {
    setData(tableData ?? []);
  }, [tableData]);

  const handleValueChange = (e: any) => {
    onValueChange(e);
    setSearchVal(e);
  };

  /* ── Avatar color cycling ── */
  const avatarColors = [
    { bg: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "#bbf7d0", color: "#15803d" },
    { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", border: "#bfdbfe", color: "#2563eb" },
    { bg: "linear-gradient(135deg,#faf5ff,#ede9fe)", border: "#ddd6fe", color: "#7c3aed" },
    { bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "#fde68a", color: "#d97706" },
    { bg: "linear-gradient(135deg,#fff1f2,#ffe4e6)", border: "#fecdd3", color: "#e11d48" },
  ];

  const getAvatarStyle = (index: number) => avatarColors[index % avatarColors.length];

  const columns = [
    columnHelper.accessor("name", {
    id: "name",
    header: () => <span className="ot-th">Organization</span>,
    cell: (info: any) => {
      const style = getAvatarStyle(info.row.index);
      const createdAt = info.row.original.created_at;

      return (
        <div className="ot-name-cell">
          <div
            className="ot-org-avatar"
            style={{
              background: style.bg,
              border: `1.5px solid ${style.border}`,
              color: style.color,
            }}
          >
            {info.getValue()?.charAt(0)?.toUpperCase() || "O"}
          </div>

          <div className="ot-name-wrapper">
            <span className="ot-name">
              {info.getValue()}
            </span>

            <span className="ot-created-under-name">
              Created: {createdAt}
            </span>
          </div>
        </div>
      );
    },
    enableSorting: true,
  }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <span className="ot-th">Email</span>,
      cell: (info) => (
        <a href={`mailto:${info.getValue()}`} className="ot-link">
          {info.getValue()}
        </a>
      ),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => <span className="ot-th">Phone</span>,
      cell: (info) => (
        <a href={`tel:${info.getValue()}`} className="ot-phone">
          {info.getValue()}
        </a>
      ),
    }),
    columnHelper.accessor("gateways", {
      id: "gateways",
      header: () => (
        <span className="ot-th">
          {roleData[0]?.organization?.value?.organization_show_method_name
            ? "Assigned Gateways"
            : "Assigned Gateway IDs"}
        </span>
      ),
      cell: (info) => {
        const paymentMethods: any = info.row.original.payment_methods || [];
        const paymentMethodsList: any = info.row.original.gateways || [];
        const gatewayCount = paymentMethods.length;

        return (
          <CustomToolTip
            content={
              paymentMethodsList.length > 0 ? (
                <div className="ot-tooltip-grid">
                  {paymentMethodsList.map((method: any, index: any) => (
                    <span key={index} className="ot-tooltip-chip">
                      {roleData[0]?.organization?.value?.organization_show_method_name
                        ? method.name
                        : method.id}
                    </span>
                  ))} 
                </div>
              ) : (
                <span className="ot-no-data">No Gateways</span>
              )
            }
            trigger={
              <div className={`ot-count-pill ot-count-pill--blue${gatewayCount === 0 ? " ot-count-pill--empty" : ""}`}>
                <span className="ot-count-dot ot-count-dot--blue" />
                <span>{gatewayCount}</span>
                {gatewayCount > 0 && (
                  <svg width="10" height="10" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </div>
            }
            header="Assigned Gateways"
          />
        );
      },
      sortingFn: (rowA, rowB, columnId) => {
        const a = (rowA.original.payment_methods || []).length;
        const b = (rowB.original.payment_methods || []).length;
        return a - b;
      },
      enableSorting: true,
    }),
    columnHelper.accessor("clients", {
      id: "clients",
      header: () => <span className="ot-th">Clients</span>,
      cell: (info) => {
        const clientList: any = info.row.original.clients || [];
        const clientCount = clientList.length;

        return (
          <CustomToolTip
            content={
              clientCount > 0 ? (
                <div className="ot-tooltip-grid">
                  {clientList.map((client: any, index: any) => (
                    <Link key={index} className="ot-tooltip-chip ot-tooltip-chip--clickable" to={`/admin/client-details/${client.id}`} >{client.name}</Link>
                    // <span 
                    //   key={index}
                    //   className=""
                    //   onClick={() =>
                    //     navigate("/admin/clients", { state: { search: client.name } })
                    //   }
                    // >
                    //   {client.name}
                    // </span>
                  ))}
                </div>
              ) : (
                <span className="ot-no-data">No Clients</span>
              )
            }
            trigger={
              <div className={`ot-count-pill ot-count-pill--green${clientCount === 0 ? " ot-count-pill--empty" : ""}`}>
                <span className="ot-count-dot ot-count-dot--green" />
                <span>{clientCount}</span>
                {clientCount > 0 && (
                  <svg width="10" height="10" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </div>
            }
            header="Assigned Clients"
          />
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = (rowA.original.clients || []).length;
        const b = (rowB.original.clients || []).length;
        return a - b;
      },
      enableSorting: true,
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => <span className="ot-th">Description</span>,
      cell: (info) => (
        <p className="ot-desc">{info.getValue()}</p>
      ),
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: () => <span className="ot-th">Actions</span>,
      cell: (info) => (
        <div className="ot-actions">
          <CreateOrgModal
            fetchOrganizations={fetchOrganizations}
            fetchAllOrganizations={fetchAllOrganizations}
            allGateways={allGateways}
            id={info.getValue()}
            roleData={roleData}
            infoData={info}
          />
          <CreateOrgModal
            fetchOrganizations={fetchOrganizations}
            fetchAllOrganizations={fetchAllOrganizations}
            id={info.getValue()}
            allGateways={allGateways}
            info={true}
            roleData={roleData}
            infoData={info}
          />
          {roleData?.[0]?.organization?.value?.delete_organization && (
            <DeleteOrgModal
              fetchOrganizations={fetchOrganizations}
              fetchAllOrganizations={fetchAllOrganizations}
              id={info.getValue()}
              name={info?.row?.original?.name ?? ""}
              clients={info.row.original.clients}
              allOrg={allOrg}
            />
          )}
        </div>
      ),
      enableSorting: false,
    }),
  ];

  /* Columns without action column for limited-permission users */
  const columnsNonAction = columns.slice(0, 5);

  const hasActionPerms =
    roleData?.[0]?.organization?.value?.view_organization ||
    roleData?.[0]?.organization?.value?.edit_organization ||
    roleData?.[0]?.organization?.value?.delete_organization;

  const table = useReactTable({
    data,
    columns: hasActionPerms ? columns : columnsNonAction,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="ot-card">
      {/* Header */}
      <div className="ot-header">
        <div className="ot-header-left">
          <div className="ot-header-icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <div>
            <h3 className="ot-title">All Organizations</h3>
            <p className="ot-subtitle">
              {totalItems > 0
                ? `${totalItems} total organization${totalItems !== 1 ? "s" : ""}`
                : "No organizations found"}
            </p>
          </div>
        </div>
        <div className="ot-search-wrap">
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* Table */}
      <div className="ot-table-wrap">
        {props.isLoading ? (
          <div className="ot-loading">
            <div className="ot-spinner" />
            <p className="ot-loading-txt">Loading organizations…</p>
          </div>
        ) : (
          <table className="ot-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      colSpan={h.colSpan}
                      onClick={h.column.getToggleSortingHandler()}
                      className="ot-thead-th"
                    >
                      <div className="ot-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="ot-sort-icon">
                            {h.column.getIsSorted() === "asc" ? (
                              <BsArrowUp size={11} />
                            ) : h.column.getIsSorted() === "desc" ? (
                              <BsArrowDown size={11} />
                            ) : (
                              <RiArrowUpDownFill size={11} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr
                    key={row.id}
                    className={`ot-tbody-tr${ri % 2 !== 0 ? " ot-tbody-tr--alt" : ""}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="ot-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>
                    <div className="ot-empty">
                      <div className="ot-empty-icon">
                        <svg
                          width="26"
                          height="26"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                      </div>
                      <p className="ot-empty-title">No organizations found</p>
                      <p className="ot-empty-sub">
                        Try adjusting your search or add a new organization
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="ot-pagination-wrap">
        <Pagination
          setPage={setPage}
          page={page}
          totalpage={totalpage}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          arraySize={[50, 100, 200]}
        />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .ot-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15,23,42,0.06);
        }

        /* Header */
        .ot-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
        }
        .ot-header-left { display: flex; align-items: center; gap: 12px; }
        .ot-header-icon {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .ot-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; margin-bottom: 2px; line-height: 22px; }
        .ot-subtitle { font-size: 12px; color: #94a3b8; font-weight: 500; }
        .ot-search-wrap { min-width: 220px; }

        /* Table container */
        .ot-table-wrap { overflow-x: auto; }
        .ot-table-wrap::-webkit-scrollbar { height: 4px; }
        .ot-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .ot-table { width: 100%; border-collapse: collapse; }

        /* Head */
        .ot-thead-th {
          padding: 12px 16px 10px;
          background: #f8fafc;
          border-bottom: 1.5px solid #e2e8f0;
          text-align: left; cursor: pointer; white-space: nowrap; user-select: none;
        }
        .ot-thead-th:hover { background: #f1f5f9; }
        .ot-th-inner { display: flex; align-items: center; gap: 5px; }
        .ot-th { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; }
        .ot-sort-icon { color: #94a3b8; display: flex; align-items: center; transition: color 0.15s; }
        .ot-thead-th:hover .ot-sort-icon { color: #2563eb; }
        .ot-name-wrapper {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .ot-created-under-name {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 500;
        }
        /* Rows */
        .ot-tbody-tr { transition: background 0.12s; }
        .ot-tbody-tr:hover { background: #f8fafc; }
        .ot-tbody-tr--alt { background: #fafbfc; }
        .ot-tbody-tr--alt:hover { background: #f1f5f9; }
        .ot-tbody-td { padding: 13px 16px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }

        /* Cells */
        .ot-name-cell { display: flex; align-items: center; gap: 9px; }
        .ot-org-avatar {
          width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700;
        }
        .ot-name { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; }
        .ot-link { font-size: 13px; font-weight: 500; color: #2563eb; text-decoration: none; }
        .ot-link:hover { text-decoration: underline; }
        .ot-phone { font-size: 13px; font-weight: 500; color: #0f172a; text-decoration: none; white-space: nowrap; }
        .ot-desc { font-size: 13px; color: #64748b; font-weight: 500; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ot-date { font-size: 13px; color: #64748b; font-weight: 500; white-space: nowrap; }

        /* Count pills */
        .ot-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 8px;
          font-size: 12.5px; font-weight: 700; cursor: pointer;
          transition: all .15s;
        }
        .ot-count-pill--blue {
          background: linear-gradient(135deg,#eff6ff,#dbeafe);
          border: 1.5px solid #bfdbfe; color: #1d4ed8;
        }
        .ot-count-pill--blue:hover { background: #dbeafe; border-color: #93c5fd; }
        .ot-count-pill--green {
          background: linear-gradient(135deg,#f0fdf4,#dcfce7);
          border: 1.5px solid #bbf7d0; color: #15803d;
        }
        .ot-count-pill--green:hover { background: #dcfce7; border-color: #86efac; }
        .ot-count-pill--empty {
          background: #f8fafc; border: 1.5px solid #e2e8f0; color: #94a3b8; cursor: default;
        }
        .ot-count-pill--empty:hover { background: #f8fafc; border-color: #e2e8f0; }
        .ot-count-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
        .ot-count-dot--blue { background: #2563eb; }
        .ot-count-dot--green { background: #16a34a; }
        .ot-count-pill--empty .ot-count-dot--blue,
        .ot-count-pill--empty .ot-count-dot--green { background: #cbd5e1; }

        /* Tooltip internals */
        .ot-tooltip-grid { display: flex; flex-wrap: wrap; gap: 6px; }
        .ot-tooltip-chip {
          font-size: 11.5px; font-weight: 600;
          padding: 4px 8px; border-radius: 6px;
          background: #f1f5f9; border: 1px solid #e2e8f0; color: #374151;
        }
        .ot-tooltip-chip--clickable { cursor: pointer; }
        .ot-tooltip-chip--clickable:hover { background: #dbeafe; border-color: #bfdbfe; color: #1d4ed8; }
        .ot-no-data { font-size: 12px; color: #94a3b8; font-style: italic; }

        /* Actions */
        .ot-actions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

        /* Empty */
        .ot-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px 24px; gap: 10px;
        }
        .ot-empty-icon {
          width: 52px; height: 52px; border-radius: 13px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          display: flex; align-items: center; justify-content: center; color: #cbd5e1;
        }
        .ot-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .ot-empty-sub { font-size: 12.5px; color: #94a3b8; font-weight: 500; text-align: center; }

        /* Loading */
        .ot-loading {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 56px; gap: 12px;
        }
        .ot-spinner {
          width: 28px; height: 28px;
          border: 3px solid #e2e8f0; border-top-color: #2563eb;
          border-radius: 50%; animation: otSpin 0.7s linear infinite;
        }
        @keyframes otSpin { to { transform: rotate(360deg); } }
        .ot-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }

        /* Pagination */
        .ot-pagination-wrap { padding: 12px 16px; border-top: 1px solid #f1f5f9; background: #fafbfc; }

        @media (max-width: 700px) {
          .ot-header { flex-direction: column; align-items: flex-start; }
          .ot-search-wrap { width: 100%; }
          .ot-thead-th, .ot-tbody-td { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
}

export default OrgTable;